import React, { useEffect, useState, createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { db, isFirebaseEnabled } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
export type SubscriptionTier = 'free' | 'premium';
interface SubscriptionLimits {
  maxActiveQuests: number;
  maxCollaborativeQuests: number;
  canUseCustomCategories: boolean;
  canUseAdvancedFocus: boolean;
  canUseAIFeatures: boolean;
  canExportData: boolean;
  canUseCustomThemes: boolean;
  canUseRecurringQuests: boolean;
  canUseTeamWorkspaces: boolean;
}
interface SubscriptionContextType {
  tier: SubscriptionTier;
  limits: SubscriptionLimits;
  loading: boolean;
  checkLimit: (feature: keyof SubscriptionLimits) => boolean;
  upgradeRequired: () => void;
}
const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);
const FREE_LIMITS: SubscriptionLimits = {
  maxActiveQuests: 5,
  maxCollaborativeQuests: 1,
  canUseCustomCategories: false,
  canUseAdvancedFocus: false,
  canUseAIFeatures: false,
  canExportData: false,
  canUseCustomThemes: false,
  canUseRecurringQuests: false,
  canUseTeamWorkspaces: false
};
const PREMIUM_LIMITS: SubscriptionLimits = {
  maxActiveQuests: Infinity,
  maxCollaborativeQuests: Infinity,
  canUseCustomCategories: true,
  canUseAdvancedFocus: true,
  canUseAIFeatures: true,
  canExportData: true,
  canUseCustomThemes: true,
  canUseRecurringQuests: true,
  canUseTeamWorkspaces: true
};
export function SubscriptionProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>('free');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!user) {
      setTier('free');
      setLoading(false);
      return;
    }
    if (!isFirebaseEnabled || !db) {
      // Local mode - default to free
      setTier('free');
      setLoading(false);
      return;
    }
    // Fetch user subscription from Firestore
    const fetchSubscription = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setTier(data.subscriptionTier || 'free');
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, [user]);
  const limits = tier === 'premium' ? PREMIUM_LIMITS : FREE_LIMITS;
  const checkLimit = (feature: keyof SubscriptionLimits): boolean => {
    return limits[feature] as boolean;
  };
  const upgradeRequired = () => {
    // This will be handled by opening the upgrade modal
    console.log('Upgrade required');
  };
  const value = {
    tier,
    limits,
    loading,
    checkLimit,
    upgradeRequired
  };
  return <SubscriptionContext.Provider value={value} data-id="element-603">
      {children}
    </SubscriptionContext.Provider>;
}
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}